// Replays a queued GatedAction after the user signs up.
// (PR2 / PUBLIC-DECKS plan, §5.3 + §6.)
//
// Mounted once at the top of DeckDetail. On mount (and any time the
// signed-in state flips to true), it reads the localStorage
// queued-action slot. If the slot is valid AND matches the current
// page's (owner, deckId), it clears the slot and dispatches the
// action via a callback the parent registers per kind.
//
// Validation gates (any failure = silent clear or no-op):
//   - User is signed in (else: leave slot, ActionReplayer will retry
//     on next mount in case the user is mid-Clerk-redirect)
//   - TTL not expired (peekQueuedAction handles this)
//   - action kind in the allow-list (peekQueuedAction handles this)
//   - deckId matches current page → clear + dispatch
//   - deckId mismatch → leave slot intact (the queued action is for a
//     different deck the user might still navigate back to). It'll
//     either fire there or expire silently.
//
// For PR2, the only `action: "ask"` callback is wired live. The
// "playtest" and "build-similar" callbacks are accepted but stubbed
// in the parent (DeckBody) — when those flows are wired up later
// the only change needed is non-null callback handlers.

import { useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import {
  clearQueuedAction,
  peekQueuedAction,
  type QueuedAction,
  type QueuedActionKind,
} from "../lib/queued-action";

export interface ActionReplayerProps {
  /** Owner username from useParams() — must match the queued
   *  entry's `owner` exactly or replay is skipped. */
  owner: string;
  /** Deck slug from useParams() — must match the queued entry's
   *  `deckId` exactly or replay is skipped. */
  deckId: string;
  /** Per-kind dispatchers. Undefined entries are treated as
   *  unsupported (slot is silently cleared so it doesn't sit there
   *  forever). */
  handlers: Partial<Record<QueuedActionKind, (q: QueuedAction) => void>>;
}

export default function ActionReplayer({
  owner,
  deckId,
  handlers,
}: ActionReplayerProps) {
  const { isSignedIn, isLoaded } = useUser();

  useEffect(() => {
    // Wait for Clerk to settle. While loading, isSignedIn can be
    // undefined and a premature read would never see the post-signup
    // state. Once loaded, if still anonymous, do nothing — the
    // GatedAction modal hasn't completed yet (or the user closed it)
    // and we want the slot to persist until TTL.
    if (!isLoaded) return;
    if (!isSignedIn) return;

    const queued = peekQueuedAction();
    if (!queued) return;

    // Cross-check (owner, deckId) — never replay an action queued
    // for a different deck on this page. The queued entry stays in
    // storage (it might still fire when the user navigates to its
    // target page within TTL_MS).
    if (queued.owner !== owner || queued.deckId !== deckId) return;

    const handler = handlers[queued.action];
    // Always clear the slot whether or not we have a handler. Leaving
    // a stale entry around for an unsupported action would cause the
    // user to land in a weird state: they think their click was
    // saved but nothing ever happens. Clearing is the safer default.
    clearQueuedAction();
    if (handler) handler(queued);
    // We deliberately don't include `handlers` in deps — the parent
    // re-creates the object on every render, which would cause
    // double-replay. The owner+deckId+isSignedIn change cadence is
    // the right trigger granularity. If a parent ever needs to
    // hot-swap handlers, it should remount ActionReplayer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn, owner, deckId]);

  return null;
}
